
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MoreHorizontal, Edit, Trash2, Users } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { CustomRole, useRolePermissions, useDeleteRole } from '@/hooks/useRoles';
import { useToast } from '@/hooks/use-toast';

interface RolesTableProps {
  roles: CustomRole[] | undefined;
  onEditRole: (role: CustomRole) => void;
  onAssignRole: (role: CustomRole) => void;
}

export function RolesTable({ roles, onEditRole, onAssignRole }: RolesTableProps) {
  if (!roles) {
    return null;
  }

  return (
    <div className="border rounded-lg">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>اسم الدور</TableHead>
            <TableHead>الوصف</TableHead>
            <TableHead>اللون</TableHead>
            <TableHead>عدد الصلاحيات</TableHead>
            <TableHead>تاريخ الإنشاء</TableHead>
            <TableHead>العمليات</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {roles.map((role) => (
            <RoleRow key={role.id} role={role} onEdit={onEditRole} onAssign={onAssignRole} />
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function RoleRow({ role, onEdit, onAssign }: { role: CustomRole; onEdit: (role: CustomRole) => void; onAssign: (role: CustomRole) => void }) {
  const { data: permissions } = useRolePermissions(role.id);
  const deleteRole = useDeleteRole();
  const { toast } = useToast();
  const enabledPermissions = permissions?.filter(p => p.permission_value).length || 0;

  const handleDelete = async () => {
    if (window.confirm(`هل أنت متأكد من حذف الدور "${role.name}"؟`)) {
      try {
        await deleteRole.mutateAsync(role.id);
        toast({
          title: "تم الحذف",
          description: "تم حذف الدور بنجاح",
        });
      } catch (error) {
        console.error('Error deleting role:', error);
        toast({
          title: "خطأ",
          description: "حدث خطأ أثناء حذف الدور",
          variant: "destructive",
        });
      }
    }
  };

  return (
    <TableRow>
      <TableCell className="font-medium">{role.name}</TableCell>
      <TableCell className="text-gray-600">{role.description || '-'}</TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <div 
            className="w-4 h-4 rounded-full border" 
            style={{ backgroundColor: role.color }}
          />
          <span className="text-xs text-gray-500">{role.color}</span>
        </div>
      </TableCell>
      <TableCell>
        <Badge variant="outline">
          {enabledPermissions} صلاحية
        </Badge>
      </TableCell>
      <TableCell className="text-gray-500">
        {new Date(role.created_at).toLocaleDateString('ar-SA')}
      </TableCell>
      <TableCell>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onEdit(role)}>
              <Edit className="mr-2 h-4 w-4" />
              تعديل
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onAssign(role)}>
              <Users className="mr-2 h-4 w-4" />
              تعيين للموظفين
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={handleDelete}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              حذف
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
}
